<template>
  <div style="flex: 1; display: flex; flex-direction: column; min-height: 0">
    <div v-if="!cfg" class="mono" style="flex: 1; display: flex; align-items: center; justify-content: center; color: var(--hsl-40)">
      {{ error || "Loading…" }}
    </div>
    <template v-else>
      <div style="flex: 1; overflow: auto; min-height: 0; padding: 16px 20px 20px">
        <div horizontal align-center style="gap: 8px; margin-bottom: 11px">
          <span class="micro">Targets</span>
          <span style="flex: 1; height: 1px; background: var(--border)"></span>
          <select v-model="newEvent" class="mv-input" style="width: 240px" :disabled="!unusedEvents.length">
            <option value="">Add event…</option>
            <option v-for="e in unusedEvents" :key="e" :value="e">{{ e }}</option>
          </select>
          <v-btn small class="addbtn" :disabled="!newEvent" @click="addTarget()">+ Add</v-btn>
        </div>
        <div style="display: flex; flex-direction: column; gap: 9px">
          <div
            v-for="event in events"
            :key="event"
            horizontal
            align-center
            nowrap
            style="gap: 9px; background: var(--back-header); border: 1px solid var(--border); border-radius: 9px; padding: 9px 12px">
            <span class="mono" style="width: 240px; flex: none; font-size: 11.5px; overflow: hidden; text-overflow: ellipsis">{{
              event
            }}</span>
            <input v-model="cfg.targets[event]" type="url" class="mv-input" placeholder="https://host/path" style="flex: 1" />
            <v-btn icon small class="delbtn" title="Remove target" @click="removeTarget(event)"><v-icon size="16">mdi-close</v-icon></v-btn>
          </div>
          <div v-if="!events.length" class="mono" style="padding: 30px; text-align: center; color: var(--hsl-40)">
            No targets — events are not delivered anywhere.
          </div>
        </div>

        <div horizontal align-center style="gap: 8px; margin: 22px 0 11px">
          <span class="micro">Delivery</span>
          <span style="flex: 1; height: 1px; background: var(--border)"></span>
        </div>
        <div horizontal style="gap: 12px">
          <div style="width: 160px">
            <div class="field-label">Max attempts</div>
            <input v-model.number="cfg.maxAttempts" type="number" min="1" step="1" class="mv-input" style="width: 100%" />
          </div>
          <div style="width: 160px">
            <div class="field-label">Retry delay (ms)</div>
            <input v-model.number="cfg.retryDelayMs" type="number" min="0" step="1000" class="mv-input" style="width: 100%" />
          </div>
        </div>

        <div horizontal align-center style="gap: 8px; margin: 22px 0 11px">
          <span class="micro">Signature secret</span>
          <span style="flex: 1; height: 1px; background: var(--border)"></span>
          <span class="mono" style="font-size: 10px; color: var(--hsl-46)">{{ secretStatus }}</span>
        </div>
        <div horizontal align-center style="gap: 12px">
          <input
            v-model="secret"
            type="password"
            autocomplete="new-password"
            class="mv-input"
            style="width: 332px"
            :disabled="clearSecret"
            :placeholder="cfg.secretSet ? 'Leave blank to keep the current secret' : 'No secret — deliveries are unsigned'" />
          <v-btn v-if="cfg.secretSet" small :class="{ delbtn: !clearSecret }" @click="clearSecret = !clearSecret">{{
            clearSecret ? "Keep secret" : "Remove secret"
          }}</v-btn>
        </div>
      </div>

      <div footer style="height: 46px; min-height: 46px; max-height: 46px; gap: 10px">
        <span class="mono" style="font-size: 10px" :style="{ color: error ? 'var(--error, #e57373)' : 'var(--hsl-46)' }">{{
          error || "Changes apply to the next delivery — no restart needed"
        }}</span>
        <div style="margin-left: auto; display: flex; gap: 8px">
          <v-btn small :disabled="saving" @click="load()">Reset</v-btn>
          <v-btn small class="addbtn" :disabled="saving" @click="save()">{{ saving ? "Saving…" : "Save" }}</v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { endpointMessage } from "@/scripts/apiErrors.js";

export default {
  data() {
    return { cfg: null, secret: "", clearSecret: false, newEvent: "", saving: false, error: "" };
  },
  computed: {
    events() {
      return Object.keys(this.cfg.targets).sort();
    },
    unusedEvents() {
      return (this.cfg.availableEvents || []).filter((e) => !(e in this.cfg.targets));
    },
    secretStatus() {
      if (this.clearSecret) return "will be removed";
      if (this.secret) return "will be replaced";
      return this.cfg.secretSet ? "set" : "not set";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.error = "";
      this.$store.state.loadWebhookConfig((error, data) => {
        if (error) this.error = endpointMessage(error);
        else this.show(data);
      });
    },
    show(config) {
      this.cfg = JSON.parse(JSON.stringify(config));
      if (!this.cfg.targets) this.cfg.targets = {};
      this.secret = "";
      this.clearSecret = false;
      this.newEvent = "";
    },
    addTarget() {
      if (!this.newEvent) return;
      this.$set(this.cfg.targets, this.newEvent, "");
      this.newEvent = "";
    },
    removeTarget(event) {
      this.$delete(this.cfg.targets, event);
    },
    // Blank URLs drop the row; a blank secret field leaves the stored secret alone.
    save() {
      const targets = {};
      for (const [event, url] of Object.entries(this.cfg.targets)) {
        if (url && url.trim()) targets[event] = url.trim();
      }
      const body = { targets, maxAttempts: this.cfg.maxAttempts, retryDelayMs: this.cfg.retryDelayMs };
      if (this.clearSecret) body.secret = "";
      else if (this.secret) body.secret = this.secret;

      this.saving = true;
      this.error = "";
      this.$store.state.saveWebhookConfig(body, (error, data) => {
        this.saving = false;
        if (error) this.error = endpointMessage(error);
        else this.show(data);
      });
    },
  },
};
</script>
